import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams, useOutletContext } from 'react-router-dom';
import ChatContainer from '../components/ChatContainer';
import api from '../services/api';
import useSessionTranscript from '../hooks/useSessionTranscript';
import './ComparePage.css';

const compareLabels = [
  { key: 'location', label: 'Location' },
  { key: 'average_area', label: 'Area' },
  { key: 'price_range_display', label: 'Price range' },
  { key: 'unit_types_display', label: 'Unit types' },
  { key: 'delivery_dates_display', label: 'Delivery dates' },
  { key: 'payment_plans', label: 'Payment plans' },
  { key: 'facilities', label: 'Amenities' },
  { key: 'why_recommended', label: 'Why recommended' },
  { key: 'lifestyle_match', label: 'Lifestyle match' },
  { key: 'consider', label: 'Consider' },
];

function formatPrice(n) {
  const m = n / 1_000_000;
  return (m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)) + 'M EGP';
}

function getDisplayValue(d, key) {
  if (key === 'price_range_display') {
    const pr = d.price_range;
    if (!pr || (pr.min == null && pr.max == null)) return '—';
    if (pr.min != null && pr.max != null) return `${formatPrice(pr.min)} – ${formatPrice(pr.max)}`;
    if (pr.min != null) return `from ${formatPrice(pr.min)}`;
    return `up to ${formatPrice(pr.max)}`;
  }
  if (key === 'unit_types_display') {
    const ut = d.unit_types;
    if (!Array.isArray(ut) || ut.length === 0) return '—';
    return ut.map(u => u.unit_type).filter(Boolean).join(', ') || '—';
  }
  if (key === 'delivery_dates_display') {
    const ut = d.unit_types;
    if (!Array.isArray(ut) || ut.length === 0) return '—';
    const dates = [...new Set(ut.flatMap(u => Array.isArray(u.delivery_dates) ? u.delivery_dates : []))];
    return dates.length > 0 ? dates.join(', ') : '—';
  }
  return d[key] || '—';
}

export default function ComparePage() {
  const navigate = useNavigate();
  const [tableOpen, setTableOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const idsParam = searchParams.get('ids');
  const compoundIds = idsParam ? idsParam.split(',').map((s) => s.trim()).filter(Boolean) : [];
  const { backendUrl, backendStatus, onRetryConnection, sessionId, setSessionId } = useOutletContext();

  // Arriving from Saved is a change of subject, not a continuation. The old
  // thread is an intake conversation about a different search; showing it next
  // to this property reads as a non-sequitur ("what's your budget?" under a
  // compound the client already saved). Start a new thread, and let the server
  // carry the requirement schema across so nothing is re-asked.
  const freshContext = !!useLocation().state?.freshContext;
  const [threadId, setThreadId] = useState(freshContext ? null : sessionId);
  const inheritSchemaFrom = freshContext ? sessionId : null;
  const loadedMessages = useSessionTranscript(threadId);

  // Once this screen creates its thread it becomes the shared one, so moving
  // on to another screen continues here rather than forking again.
  const handleThread = (id) => { setThreadId(id); setSessionId(id); };

  const [compounds, setCompounds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/api/saved-items?item_type=compound');
        const items = data.items || [];
        // Filter to only the selected compound ids
        const matched = compoundIds.length >= 2
          ? items.filter(i => compoundIds.includes(i.item_key))
          : items.slice(0, 3); // fallback: show first 3 saved
        if (!cancelled) setCompounds(matched);
      } catch (err) {
        console.error('Failed to load compounds for comparison', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [idsParam]); // eslint-disable-line react-hooks/exhaustive-deps

  const names = compounds.map(c => {
    const d = c.item_data || {};
    return d.project_name || d.name || d.title || c.item_key || 'Compound';
  });

  // item_key is a string and CompoundCard falls back to a name slug when a
  // result carries no compound_id. get_compound_details takes an int, so drop
  // anything unresolvable; below two survivors there is nothing to compare and
  // we send no focus rather than a half-set.
  const focusIds = compounds
    .map((c) => Number(c.item_data?.compound_id ?? c.item_key))
    .filter((n) => Number.isInteger(n))
    .slice(0, 3);
  const focus = focusIds.length >= 2
    ? { type: 'comparison', ids: focusIds, labels: names.slice(0, focusIds.length) }
    : null;

  const rows = compareLabels.map(({ key, label }) => ({
    label,
    values: compounds.reduce((acc, c, i) => {
      const d = c.item_data || {};
      let val = getDisplayValue(d, key);
      // Truncate long text for table readability
      if (typeof val === 'string' && val.length > 120) val = val.slice(0, 117) + '…';
      acc[i] = val;
      return acc;
    }, {}),
  }));

  if (loading) {
    return (
      <div className="compare-page">
        <div className="compare-chat">
          <ChatContainer
            backendUrl={backendUrl}
            backendStatus={backendStatus}
            onRetryConnection={onRetryConnection}
            sessionId={sessionId}
            onSessionChange={setSessionId}
          />
        </div>
        <div className="compare-table-panel">
          <h2>Loading comparison…</h2>
        </div>
      </div>
    );
  }

  if (compounds.length < 2) {
    return (
      <div className="compare-page">
        <div className="compare-chat">
          <ChatContainer
            backendUrl={backendUrl}
            backendStatus={backendStatus}
            onRetryConnection={onRetryConnection}
            sessionId={sessionId}
            onSessionChange={setSessionId}
          />
        </div>
        <div className="compare-table-panel">
          <div className="compare-empty">
            <h2>Not enough compounds</h2>
            <p>Select at least 2 saved compounds to compare them side by side.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="compare-page">
      <div className="compare-chat">
        <ChatContainer
          backendUrl={backendUrl}
          backendStatus={backendStatus}
          onRetryConnection={onRetryConnection}
          sessionId={threadId}
          onSessionChange={handleThread}
          inheritSchemaFrom={inheritSchemaFrom}
          loadedMessages={loadedMessages}
          showHeader={false}
          focus={focus}
          focusLabel={names.length === 2 ? names.join(' and ') : `these ${names.length} compounds`}
          onClearFocus={() => navigate('/')}
          suggestedChips={[
            // With focus set, the agent resolves "these" itself, so the chips
            // stay short. Without it (non-numeric ids — see focusIds above)
            // there is no referent, so they have to name their subjects or
            // they are unanswerable, which is the bug this replaced.
            focus || names.length < 2
              ? 'Which has better payment terms?'
              : `Which has better payment terms, ${names[0]} or ${names[1]}?`,
            'Compare their delivery dates',
            'Which of these is better for a family?',
          ]}
        />
      </div>
      <div className={`compare-table-panel${tableOpen ? ' open' : ''}`}>
        <button
          type="button"
          className="compare-table-toggle"
          onClick={() => setTableOpen((o) => !o)}
          aria-expanded={tableOpen}
        >
          <span>Comparing {compounds.length} compounds</span>
          <span className="compound-details-caret" aria-hidden>{tableOpen ? '\u25B4' : '\u25BE'}</span>
        </button>
        <h2 className="compare-table-heading">Comparing {compounds.length} compounds</h2>
        <div className="compare-table-wrapper">
          <table className="compare-table">
            <thead>
              <tr>
                <th className="compare-label-col">Feature</th>
                {names.map((name, i) => (
                  <th key={i}>{name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label}>
                  <td className="compare-label-col">{row.label}</td>
                  {compounds.map((_, i) => (
                    <td key={i}>{row.values[i] ?? '—'}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
